import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { MyAccount } from '../../../../../../models/my-account';
import { AccountService } from '../../../../../../services/account/account.service';
import { SimpleMessageModalComponent } from '../../../../../../components/modals/simple-message-modal/simple-message-modal.component';
import { ComponentHasUnsavedChanges } from '../../../../../../unsaved-changes-guard.service';
import { VoucherPack, VoucherPackGiftingConfig, VoucherPackStatus } from '../../../../../../models/voucher-pack';
import { VoucherPackService } from '../../../../../../services/voucher-pack/voucher-pack.service';


@Component({
  selector: 'app-voucher-pack-gifting-config-detail',
  templateUrl: './voucher-pack-gifting-config-detail.component.html',
  styleUrls: ['./voucher-pack-gifting-config-detail.component.css']
})
export class VoucherPackGiftingConfigDetailComponent implements OnInit, ComponentHasUnsavedChanges {
  public isNewConfig: boolean;
  public giftingConfig: VoucherPackGiftingConfig = {
    id: null,
    url: null,
    assigned_voucher_pack_ids: [],
    assigned_voucher_pack_names: [],
    max_future_delivery_date_days: 366,
    expiration_days: 366,
    gift_giver_terms_and_conditions: '',
    gift_recipient_terms_and_conditions: '',
    created_at: null,
    updated_at: null
  };
  public voucherPacks: VoucherPack[];

  public unsavedChanges = false;
  public errorMessage = '';
  public errors: any = {};

  public loadingExistingConfig = false;
  public loadingVoucherPacks = true;
  public loadingSubmit = false;
  public loadingDelete = false;

  public configNotFound = false;
  public serverError: number = null;

  public myAccount: MyAccount;

  public VoucherPackStatus = VoucherPackStatus;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private voucherPackService: VoucherPackService,
    private accountService: AccountService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;
    this.isNewConfig = !routeParams.hasOwnProperty('id');

    this.myAccount = this.accountService.getMyAccount();
    if (!this.myAccount.is_organization_admin) { return; }

    if (!this.isNewConfig) {
      const idInt = parseInt(routeParams.id, 10);
      if (isNaN(idInt)) {
        this.configNotFound = true;
        this.loadingExistingConfig = false;
        return;
      }

      this.loadGiftingConfig(idInt);
    }

    this.loadVoucherPacks();
  }

  hasUnsavedChanges() {
    return this.unsavedChanges;
  }

  @HostListener('window:beforeunload')
  allowUnload() {
    return !this.hasUnsavedChanges();
  }

  public getFilteredVoucherPacks(): VoucherPack[] {
    return this.voucherPacks.filter(m => {
      return (
        [
          VoucherPackStatus.draft,
          VoucherPackStatus.active
        ].includes(m.status)
      );
    })
  }

  public voucherPackSelected(voucherPack: VoucherPack) {
    return this.giftingConfig.assigned_voucher_pack_ids.includes(voucherPack.id);
  }

  public toggleVoucherPack(membershvoucherPackp: VoucherPack) {
    if (this.voucherPackSelected(membershvoucherPackp)) {
      this.giftingConfig.assigned_voucher_pack_ids.splice(
        this.giftingConfig.assigned_voucher_pack_ids.indexOf(membershvoucherPackp.id),
        1
      );
    } else {
      this.giftingConfig.assigned_voucher_pack_ids.push(membershvoucherPackp.id);
    }
    this.unsavedChanges = true;
  }

  public allVoucherPacksAreSelected() {
    for (const voucherPack of this.getFilteredVoucherPacks()) {
      if (!this.giftingConfig.assigned_voucher_pack_ids.includes(voucherPack.id)) {
        return false;
      }
    }

    return true;
  }

  public setNoVoucherPacksSelected() {
    this.giftingConfig.assigned_voucher_pack_ids = [];
    this.unsavedChanges = true;
  }

  public setAllVoucherPacksSelected() {
    this.giftingConfig.assigned_voucher_pack_ids = this.getFilteredVoucherPacks().map(m => m.id);
    this.unsavedChanges = true;
  }

  public isLoading() {
    return (
      this.loadingExistingConfig ||
      this.loadingVoucherPacks ||
      this.loadingSubmit ||
      this.loadingDelete
    )
  }

  public handleSubmit() {
    this.loadingSubmit = true;

    this.errorMessage = '';
    this.errors = {};

    let savePromise: Promise<VoucherPackGiftingConfig>;
    if (this.isNewConfig) {
      savePromise = this.voucherPackService.addVoucherPackGiftingConfig(this.giftingConfig);
    } else {
      savePromise = this.voucherPackService.updateVoucherPackGiftingConfig(this.giftingConfig);
    }

    savePromise
      .then(savedConfig => {
        return this.voucherPackService
          .assignGiftingConfigToVoucherPacks(
            savedConfig.id,
            this.giftingConfig.assigned_voucher_pack_ids
          )
          .then(() => {
            this.unsavedChanges = false;

            let successMessage: string;
            if (this.isNewConfig) {
              successMessage = 'Voucher pack gifting config created successfully.';
            } else {
              successMessage = 'Voucher pack gifting config updated successfully.';
            }

            this.router.navigate(
              ['/gifting-config'],
              {
                state: {
                  feedback: {
                    text: successMessage,
                    status: 'success'
                  }
                }
              }
            );
          })
          .catch(reason => {
            if (reason.status === 400) {
              this.errorMessage = reason.error;
            } else if (reason.status === 403) {
              if (this.isNewConfig) {
                this.errorMessage = 'You do not have permission to add voucher pack gifting configurations.';
              } else {
                this.errorMessage = 'You do not have permission to modify this voucher pack gifting configuration.';
              }
            } else {
              this.serverError = reason.status;
            }
          });
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors shown and try again.';
        } else if (reason.status === 403) {
          if (this.isNewConfig) {
            this.errorMessage = 'You do not have permission to add voucher pack gifting configurations.';
          } else {
            this.errorMessage = 'You do not have permission to modify this voucher pack gifting configuration.';
          }
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  public handleDelete() {
    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Delete Voucher Pack Gifting Configuration';
    modalRef.componentInstance.messageText = 'Are you sure you want to delete ' +
      'this voucher pack gifting config? This action cannot be undone.';
    modalRef.componentInstance.messageAlertClass = 'alert-danger';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      this.loadingDelete = true;

      this.voucherPackService
        .deleteVoucherPackGiftingConfig(this.giftingConfig)
        .then(() => {
          this.unsavedChanges = false;

          this.router.navigate(
            ['/gifting-config'],
            {
              state: {
                feedback: {
                  text: 'Voucher pack gifting configuration deleted.',
                  status: 'success'
                }
              }
            }
          )
        })
        .catch(reason => {
          if (reason.status === 400) {
            this.errors = reason.error;
            this.errorMessage = 'Please correct the errors shown and try again.';
          } else if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to delete this voucher pack gifting configuration.';
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => { this.loadingDelete = false; });
    }
  }

  private loadVoucherPacks() {
    this.loadingVoucherPacks = true;

    this.voucherPackService
      .getVoucherPacks(1, 3000, {})
      .then(result => {
        this.voucherPacks = result.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.configNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingVoucherPacks = false; });
  }

  private loadGiftingConfig(id: number) {
    this.loadingExistingConfig = true;

    this.voucherPackService
      .getVoucherPackGiftingConfig(id)
      .then(config => {
        this.giftingConfig = config;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.configNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingExistingConfig = false; });
  }
}
